// src/store/slices/cartSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cartService from '../../services/cartService';

const getInitialCartState = () => {
  try {
    return cartService.getCartState();
  } catch {
    return { items: [], total: 0, quantity: 0 };
  }
};

const initialCart = getInitialCartState();

const normalizeRemovePayload = (payload) => {
  if (typeof payload === 'string') {
    return { productId: payload };
  }
  return payload || {};
};

// Async Thunks for cart operations (localStorage only)
export const fetchCart = createAsyncThunk('cart/fetchCart', async (_, { rejectWithValue }) => {
  try {
    return { ...cartService.getCartState(), mode: 'guest' };
  } catch (err) {
    return rejectWithValue(err.message || 'Failed to load cart');
  }
});

export const addItemToCart = createAsyncThunk('cart/addItemToCart', async (productData, { rejectWithValue }) => {
  try {
    return { ...cartService.addItem(productData, productData.quantity || 1), mode: 'guest' };
  } catch (err) {
    return rejectWithValue(err.message || 'Failed to add item to cart');
  }
});

export const updateQuantity = createAsyncThunk(
  'cart/updateQuantity',
  async ({ productId, quantity, variantId = null }, { rejectWithValue }) => {
    try {
      return { ...cartService.updateItemQuantity(productId, quantity, variantId), mode: 'guest' };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update quantity');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (payload, { rejectWithValue }) => {
    try {
      const { productId, variantId = null } = normalizeRemovePayload(payload);
      return { ...cartService.removeItem(productId, variantId), mode: 'guest' };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to remove item from cart');
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: initialCart.items,
    total: initialCart.total,
    quantity: initialCart.quantity,
    loading: false,
    error: null,
    mode: 'guest',
    isCartDrawerOpen: false,
    appliedCoupon: null,
  },
  reducers: {
    // Initialize cart from localStorage when app loads
    initializeCart: (state) => {
      const cartData = cartService.getCartState();
      state.items = cartData.items;
      state.total = cartData.total;
      state.quantity = cartData.quantity;
      state.mode = 'guest';
    },
    // Clear cart (useful for logout)
    clearCartState: (state) => {
      cartService.clearCart();
      state.items = [];
      state.total = 0;
      state.quantity = 0;
      state.loading = false;
      state.error = null;
      state.mode = 'guest';
      state.appliedCoupon = null;
    },
    setAppliedCoupon: (state, action) => {
      state.appliedCoupon = action.payload;
    },
    clearAppliedCoupon: (state) => {
      state.appliedCoupon = null;
    },
    setCartDrawerOpen: (state, action) => {
      state.isCartDrawerOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.total = action.payload.total || 0;
        state.quantity = action.payload.quantity || 0;
        state.mode = 'guest';
        state.error = null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addItemToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItemToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.quantity = action.payload.quantity;
        state.mode = 'guest';
        state.isCartDrawerOpen = true;
        state.error = null;
      })
      .addCase(addItemToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuantity.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.quantity = action.payload.quantity;
        state.mode = 'guest';
        state.error = null;
      })
      .addCase(updateQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.quantity = action.payload.quantity;
        state.mode = 'guest';
        state.error = null;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { initializeCart, clearCartState, setCartDrawerOpen, setAppliedCoupon, clearAppliedCoupon } = cartSlice.actions;
export default cartSlice.reducer;